import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, apiErrorText, formatBRL, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const STATUS = {
  scheduled: { label: "Agendada", cls: "bg-accent/20 text-accent" },
  in_progress: { label: "Em execução", cls: "bg-primary/20 text-primary" },
  completed: { label: "Concluída", cls: "bg-green-500/20 text-green-400" },
  cancelled: { label: "Cancelada", cls: "bg-destructive/20 text-destructive" },
};

const KIND = {
  inspection: { label: "Inspeção", cls: "bg-primary/20 text-primary border-primary/30" },
  site_visit: { label: "Visita técnica", cls: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
};

const EMPTY = {
  client_id: "", kind: "inspection", title: "", description: "", drone_type: "",
  scheduled_start: "", scheduled_end: "",
  address_street: "", address_number: "", address_city: "", address_state: "", address_zip: "",
  status: "scheduled", price: "", notes: "",
};

export default function Inspections() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [kindFilter, setKindFilter] = useState("all"); // all | inspection | site_visit
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const params = kindFilter !== "all" ? { kind: kindFilter } : {};
    const [i, c] = await Promise.all([api.get("/inspections", { params }), api.get("/clients")]);
    setRows(i.data); setClients(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindFilter]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...EMPTY, ...r,
      scheduled_start: r.scheduled_start ? r.scheduled_start.slice(0, 16) : "",
      scheduled_end: r.scheduled_end ? r.scheduled_end.slice(0, 16) : "",
      price: r.price ?? "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: form.price === "" ? null : parseFloat(form.price),
        scheduled_start: new Date(form.scheduled_start).toISOString(),
        scheduled_end: new Date(form.scheduled_end).toISOString(),
      };
      if (editing) await api.put(`/inspections/${editing.id}`, payload);
      else await api.post("/inspections", payload);
      toast.success(editing ? "Inspeção atualizada" : "Inspeção agendada");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover inspeção?")) return;
    await api.delete(`/inspections/${id}`);
    toast.success("Removida"); load();
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <Layout
      title="Inspeções"
      subtitle="Missões de voo e visitas técnicas de orçamento"
      actions={<Button onClick={openNew} data-testid="new-inspection-btn" className="gap-2"><Plus size={16} weight="bold" /> Novo agendamento</Button>}
    >
      <div className="mb-6 flex items-center gap-3">
        <div className="flex border border-border rounded-md overflow-hidden">
          {[["all", "Todos"], ["inspection", "Inspeções"], ["site_visit", "Visitas técnicas"]].map(([k, lb]) => (
            <button key={k} onClick={() => setKindFilter(k)}
              className={`px-4 py-2 text-sm font-bold transition-colors ${kindFilter === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
              data-testid={`filter-kind-${k}`}
            >{lb}</button>
          ))}
        </div>
      </div>

      <div className="tech-card overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr className="text-left">
              <th className="label-tech text-muted-foreground p-4">Tipo</th>
              <th className="label-tech text-muted-foreground p-4">Missão</th>
              <th className="label-tech text-muted-foreground p-4">Cliente</th>
              <th className="label-tech text-muted-foreground p-4">Local</th>
              <th className="label-tech text-muted-foreground p-4">Data</th>
              <th className="label-tech text-muted-foreground p-4">Valor</th>
              <th className="label-tech text-muted-foreground p-4">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody data-testid="inspections-list">
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="p-12 text-center text-muted-foreground">Nenhum agendamento</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-border hover:bg-secondary/30" data-testid={`inspection-row-${r.id}`}>
                <td className="p-4"><Badge variant="outline" className={KIND[r.kind || "inspection"]?.cls}>{KIND[r.kind || "inspection"]?.label}</Badge></td>
                <td className="p-4">
                  <div className="font-bold">{r.title}</div>
                  <div className="text-xs text-muted-foreground">{r.drone_type || ""}</div>
                </td>
                <td className="p-4 text-sm">{clientName(r.client_id)}</td>
                <td className="p-4 text-sm">{r.address_city ? `${r.address_city}/${r.address_state || ""}` : "—"}</td>
                <td className="p-4 text-sm mono">{formatDateTime(r.scheduled_start)}</td>
                <td className="p-4 text-sm mono">{r.price ? formatBRL(r.price) : "—"}</td>
                <td className="p-4">
                  <Badge className={STATUS[r.status]?.cls}>{STATUS[r.status]?.label || r.status}</Badge>
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-inspection-${r.id}`}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r.id)}><Trash size={16} className="text-destructive" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar agendamento" : "Novo agendamento"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-tech">Tipo *</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="inspection-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inspection">Inspeção (voo)</SelectItem>
                  <SelectItem value="site_visit">Visita técnica (orçamento)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Cliente *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="inspection-client"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Título / Descrição da missão *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="inspection-title" />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Início *</Label>
              <Input type="datetime-local" value={form.scheduled_start} onChange={(e) => setForm({ ...form, scheduled_start: e.target.value })} data-testid="inspection-start" />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Fim *</Label>
              <Input type="datetime-local" value={form.scheduled_end} onChange={(e) => setForm({ ...form, scheduled_end: e.target.value })} data-testid="inspection-end" />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Tipo de drone</Label>
              <Input value={form.drone_type} onChange={(e) => setForm({ ...form, drone_type: e.target.value })} placeholder="DJI Mavic 3 Pro" />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Valor (R$)</Label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="inspection-price" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Endereço da inspeção</Label>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <Input className="md:col-span-3" placeholder="Rua" value={form.address_street} onChange={(e) => setForm({ ...form, address_street: e.target.value })} />
                <Input placeholder="Número" value={form.address_number} onChange={(e) => setForm({ ...form, address_number: e.target.value })} />
                <Input placeholder="Cidade" value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} />
                <Input placeholder="UF" maxLength={2} value={form.address_state} onChange={(e) => setForm({ ...form, address_state: e.target.value.toUpperCase() })} />
                <Input placeholder="CEP" value={form.address_zip} onChange={(e) => setForm({ ...form, address_zip: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Descrição / Escopo</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Observações internas</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-inspection-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
